import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import SearchableCombobox from '@/components/ui/searchable-combobox';
import { ArrowLeft, Plus, X, Package, Send, Save, FileText } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NuevaPublicacion = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [frigorificos, setFrigorificos] = useState([]);
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPdfDialog, setShowPdfDialog] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');

  // Form state
  const [frigorificoId, setFrigorificoId] = useState('');
  const [selectedFrigo, setSelectedFrigo] = useState(null);
  const [plantaNumero, setPlantaNumero] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [tipo, setTipo] = useState('CFR');
  const [estimadoEmbarque, setEstimadoEmbarque] = useState('');
  const [fcls, setFcls] = useState([{ productoId: '', descripcion: '', peso: '', unidadPeso: 'kg', precio: '' }]);

  useEffect(() => {
    const init = async () => {
      try {
        const [frigosRes, prodRes] = await Promise.all([
          axios.get(`${API}/frigorificos`),
          axios.get(`${API}/productos`)
        ]);
        const frigos = frigosRes.data;
        setFrigorificos(frigos);
        setProductos(prodRes.data);

        if (id) {
          const pubRes = await axios.get(`${API}/publicaciones/${id}`);
          const pub = pubRes.data;
          setFrigorificoId(pub.frigorificoId);
          const frigo = frigos.find(f => f.id === pub.frigorificoId);
          setSelectedFrigo(frigo || null);
          setPlantaNumero(pub.plantaNumero ? String(pub.plantaNumero) : '');
          setPaymentTerms(pub.paymentTerms || '');
          setTipo(pub.tipo);
          setEstimadoEmbarque(pub.estimadoEmbarque || '');
          if (pub.fcls && pub.fcls.length > 0) {
            setFcls(pub.fcls.map(f => ({
              productoId: f.productoId || '',
              descripcion: f.descripcion || '',
              peso: f.peso || '',
              unidadPeso: f.unidadPeso || 'kg',
              precio: f.precio || ''
            })));
          }
        }
      } catch (error) {
        toast.error('Error al cargar datos iniciales');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [id]);

  const handleFrigorificoChange = (id) => {
    setFrigorificoId(id);
    const frigo = frigorificos.find(f => f.id === id);
    setSelectedFrigo(frigo || null);
    
    if (frigo?.plantas?.length === 1) {
      setPlantaNumero(String(frigo.plantas[0].numero));
    } else {
      setPlantaNumero('');
    }

    if (frigo?.paymentTerms?.length === 1) {
      setPaymentTerms(frigo.paymentTerms[0]);
    } else {
      setPaymentTerms('');
    }
  };

  const addFcl = () => {
    setFcls([...fcls, { productoId: '', descripcion: '', peso: '', unidadPeso: 'kg', precio: '' }]);
  };

  const removeFcl = (idx) => {
    if (fcls.length <= 1) return;
    setFcls(fcls.filter((_, i) => i !== idx));
  };

  const updateFcl = (idx, field, value) => {
    const updated = [...fcls];
    if (field === 'productoId') {
      const prod = productos.find(p => p.id === value);
      updated[idx] = { ...updated[idx], productoId: value, descripcion: prod ? prod.nombre : '' };
    } else {
      updated[idx] = { ...updated[idx], [field]: value };
    }
    setFcls(updated);
  };

  const handleSubmit = async (estado) => {
    if (!frigorificoId) { toast.error('Seleccioná un frigorífico'); return; }
    if (!tipo) { toast.error('Seleccioná el tipo'); return; }

    const validFcls = fcls.filter(f => f.descripcion.trim());
    if (validFcls.length === 0 && estado === 'Enviada') {
      toast.error('Agregá al menos un FCL');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        frigorificoId,
        plantaNumero: plantaNumero ? parseInt(plantaNumero) : null,
        paymentTerms: paymentTerms || null,
        tipo,
        fcls: validFcls.map(f => ({
          productoId: f.productoId || null,
          descripcion: f.descripcion.trim(),
          peso: parseFloat(f.peso) || 0,
          unidadPeso: f.unidadPeso,
          precio: parseFloat(f.precio) || 0,
        })),
        estimadoEmbarque: estimadoEmbarque.trim(),
        estadoPublicacion: estado,
      };

      if (id) {
        await axios.put(`${API}/publicaciones/${id}`, payload);
        toast.success(estado === 'Enviada' ? 'Oferta enviada' : 'Borrador actualizado');
        navigate(`/publicaciones/${id}`);
      } else {
        const res = await axios.post(`${API}/publicaciones`, payload);
        toast.success(estado === 'Enviada' ? 'Oferta enviada' : 'Borrador guardado');
        navigate(`/publicaciones/${res.data.id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const totalPrecio = fcls.reduce((sum, f) => sum + (parseFloat(f.precio) || 0), 0);
  const totalPeso = fcls.reduce((sum, f) => sum + (parseFloat(f.peso) || 0), 0);

  const frigorificoOptions = frigorificos.map(f => ({ value: f.id, label: `${f.nombre} — ${f.pais}` }));
  const productoOptions = productos.map(p => ({ value: p.id, label: p.nombre }));

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><div className="text-muted-foreground">Cargando...</div></div>;
  }

  return (
    <div className="space-y-6 bg-background min-h-screen">
      <div className="bg-background border-b">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-6">
          <Button variant="ghost" className="mb-4" onClick={() => navigate('/publicaciones')}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Volver
          </Button>
          <h1 className="text-3xl font-semibold tracking-tight">{id ? 'Editar Oferta' : 'Nueva Oferta'}</h1>
          <p className="text-muted-foreground mt-1">{id ? 'Editar la oferta en borrador' : 'Crear una nueva oferta de productos'}</p>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 space-y-6 pb-12">
        {/* Frigorífico & Basic Info */}
        <Card className="p-6 space-y-5">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" /> Datos de la Oferta
          </h2>

          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label>Frigorífico *</Label>
              <SearchableCombobox
                value={frigorificoId}
                onSelect={handleFrigorificoChange}
                options={frigorificoOptions}
                placeholder="Seleccionar frigorífico..."
              />
            </div>

            <div className="space-y-2">
              <Label>Planta</Label>
              <Select value={plantaNumero} onValueChange={setPlantaNumero} disabled={!selectedFrigo}>
                <SelectTrigger><SelectValue placeholder="Seleccionar planta" /></SelectTrigger>
                <SelectContent>
                  {(selectedFrigo?.plantas || []).map(p => (
                    <SelectItem key={p.id} value={String(p.numero)}>#{p.numero}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Forma de Pago</Label>
              <Select value={paymentTerms} onValueChange={setPaymentTerms} disabled={!selectedFrigo}>
                <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>
                  {(selectedFrigo?.paymentTerms || []).map((pt, i) => (
                    <SelectItem key={i} value={pt}>{pt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Tipo *</Label>
              <Select value={tipo} onValueChange={setTipo}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CFR">CFR</SelectItem>
                  <SelectItem value="CIF">CIF</SelectItem>
                  <SelectItem value="FOB">FOB</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Estimado de Embarque</Label>
              <Input type="date" value={estimadoEmbarque} onChange={(e) => setEstimadoEmbarque(e.target.value)} />
            </div>
          </div>
        </Card>

        {/* FCLs */}
        <Card className="p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">FCLs ({fcls.length})</h2>
            <Button variant="outline" size="sm" onClick={addFcl}><Plus className="h-4 w-4 mr-1" /> Agregar FCL</Button>
          </div>

          <div className="space-y-4">
            {fcls.map((fcl, idx) => (
              <div key={idx} className="relative border rounded-lg p-4 space-y-3 bg-accent/30">
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="text-xs">FCL {idx + 1}</Badge>
                  {fcls.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removeFcl(idx)} className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive">
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs flex-1">Producto</Label>
                    {(() => {
                      const prod = productos.find(p => p.id === fcl.productoId);
                      if (prod && prod.ficha_tecnica_url) {
                        return (
                          <Button variant="ghost" size="sm" className="h-6 px-2 py-0 text-primary" onClick={() => { setPdfUrl(BACKEND_URL + prod.ficha_tecnica_url); setShowPdfDialog(true); }}>
                            <FileText className="h-3 w-3 mr-1" /> Ver Ficha
                          </Button>
                        );
                      }
                      return null;
                    })()}
                  </div>
                  <SearchableCombobox
                    value={fcl.productoId}
                    onSelect={(v) => updateFcl(idx, 'productoId', v)}
                    options={productoOptions}
                    placeholder="Seleccionar producto..."
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Peso</Label>
                    <Input type="number" value={fcl.peso} onChange={(e) => updateFcl(idx, 'peso', e.target.value)} placeholder="27000" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Unidad</Label>
                    <Select value={fcl.unidadPeso} onValueChange={(v) => updateFcl(idx, 'unidadPeso', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="kg">kg</SelectItem>
                        <SelectItem value="tn">tn</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Precio USD</Label>
                    <Input type="number" value={fcl.precio} onChange={(e) => updateFcl(idx, 'precio', e.target.value)} placeholder="4500" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="flex items-center justify-end gap-6 text-sm pt-3 border-t">
            <div>Peso total: <strong>{totalPeso.toLocaleString()} {fcls[0]?.unidadPeso || 'kg'}</strong></div>
            <div>Total: <strong>USD {totalPrecio.toLocaleString()}</strong></div>
          </div>
        </Card>

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={() => handleSubmit('Borrador')} disabled={saving}>
            <Save className="h-4 w-4 mr-2" /> Guardar Borrador
          </Button>
          <Button onClick={() => handleSubmit('Enviada')} disabled={saving}>
            <Send className="h-4 w-4 mr-2" /> Publicar
          </Button>
        </div>
      </div>

      {/* PDF Dialog */}
      <Dialog open={showPdfDialog} onOpenChange={setShowPdfDialog}>
        <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Ficha Técnica</DialogTitle>
          </DialogHeader>
          <div className="flex-1 w-full bg-muted rounded-md overflow-hidden">
            {pdfUrl && (
              <iframe src={pdfUrl} className="w-full h-full" title="Ficha Técnica" />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NuevaPublicacion;
